import * as React from 'react';
import './ProfileAvatar.scss';
import Avatar from '@material-ui/core/Avatar';
import { Chip, CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { alertError, alertSuccess } from '../../store/alerts/actions';
import { getInitials } from '../../shared/string';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

export interface ProfileAvatarProps {
    src?: string | null;
    alt: string;
    displayName: string;
    username?: string;
    email?: string;
    isEditable?: boolean;
    uploading?: boolean;
    onUploadSuccess: (url: string, photoFileId: string) => void;
    onRemove?: () => void;
    uploadHandler: (file: File) => Promise<{ url: string; photoFileId: string }>;
    accountType?: string;
    accountTypeIcon?: React.ReactNode;
    editLabel?: string;
    removeLabel?: string;
    onlyImageFilesAllowedMessage: string;
    fileSizeLimitMessage: string;
    profilePictureUpdatedMessage: string;
    profilePictureRemovedMessage?: string;
    errorUploadingMessage: string;
}

const ProfileAvatar: React.FC<ProfileAvatarProps> = React.memo(({
    src,
    alt,
    displayName,
    username,
    email,
    isEditable = false,
    uploading = false,
    onUploadSuccess,
    onRemove,
    uploadHandler,
    accountType,
    accountTypeIcon,
    editLabel = 'Edit',
    removeLabel = 'Remove',
    onlyImageFilesAllowedMessage,
    fileSizeLimitMessage,
    profilePictureUpdatedMessage,
    profilePictureRemovedMessage = 'Profile picture removed',
    errorUploadingMessage,
}) => {
    const dispatch = useDispatch();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const [isRemoving, setIsRemoving] = React.useState(false);
    const [isImageLoading, setIsImageLoading] = React.useState(false);
    const [pendingImageUrl, setPendingImageUrl] = React.useState<string | null>(null);

    const initials = React.useMemo(
        () => getInitials(displayName, username, email),
        [displayName, username, email]
    );

    // Track when src changes to start loading the new image
    React.useEffect(() => {
        if (src && src !== pendingImageUrl) {
            setIsImageLoading(true);
            setPendingImageUrl(src);
        } else if (!src && pendingImageUrl) {
            // Avatar was removed, reset loading state
            setIsImageLoading(false);
            setPendingImageUrl(null);
        }
    }, [src, pendingImageUrl]);

    const handleImageLoad = React.useCallback(() => {
        setIsImageLoading(false);
    }, []);

    const handleImageError = React.useCallback(() => {
        setIsImageLoading(false);
    }, []);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            dispatch(alertError(onlyImageFilesAllowedMessage));
            return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            dispatch(alertError(fileSizeLimitMessage));
            return;
        }

        setIsUploading(true);
        setIsImageLoading(true);
        try {
            const response = await uploadHandler(file);
            onUploadSuccess(response.url, response.photoFileId);
            dispatch(alertSuccess(profilePictureUpdatedMessage));
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            dispatch(alertError(errorUploadingMessage));
            setIsImageLoading(false);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEditable && !isUploading && !uploading && !isRemoving) {
            fileInputRef.current?.click();
        }
    };

    const handleRemoveClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!onRemove || isUploading || uploading || isRemoving) return;

        setIsRemoving(true);
        setIsImageLoading(false);
        setPendingImageUrl(null);
        try {
            await onRemove();
            dispatch(alertSuccess(profilePictureRemovedMessage));
        } catch (error) {
            console.error('Error removing profile picture:', error);
            dispatch(alertError(errorUploadingMessage));
        } finally {
            setIsRemoving(false);
        }
    };

    const isLoading = isUploading || uploading || isRemoving || isImageLoading;
    const hasImage = !!src;

    return (
        <div className="avatarContainer">
            <div className={`avatarButton ${isEditable ? 'isEditable' : ''}`}>
                <Avatar
                    src={src || undefined}
                    alt={alt}
                    className="profileAvatar"
                    imgProps={{
                        onLoad: handleImageLoad,
                        onError: handleImageError,
                    } as React.ImgHTMLAttributes<HTMLImageElement>}
                >
                    {!src && initials}
                </Avatar>
                {isEditable && (
                    <div className={`avatarOverlay ${isLoading ? 'showLoading' : ''}`}>
                        {isLoading ? (
                            <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                            <div className="avatarActions">
                                <button
                                    type="button"
                                    className="avatarActionButton"
                                    onClick={handleEditClick}
                                    disabled={isLoading}
                                    aria-label={editLabel}
                                    title={editLabel}
                                >
                                    <EditIcon fontSize="small" />
                                </button>
                                {hasImage && onRemove && (
                                    <button
                                        type="button"
                                        className="avatarActionButton"
                                        onClick={handleRemoveClick}
                                        disabled={isLoading}
                                        aria-label={removeLabel}
                                        title={removeLabel}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {accountType && accountTypeIcon && (
                <Chip
                    icon={accountTypeIcon as any}
                    label={accountType}
                    size="small"
                    className="accountTypeChip"
                />
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="avatarFileInput"
                onChange={handleFileSelect}
            />
        </div>
    );
});

ProfileAvatar.displayName = 'ProfileAvatar';

export default ProfileAvatar;
